import { useEffect, useState } from 'react';
import { Box, Button, Grid, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { grey, amber, blue } from '@mui/material/colors';
import ConfirmDialog from '../../../../../common/ConfirmDialog';
import { useAppContext } from "../../../../../../contexts/AppContext";
import { useCommonItems } from "../../../../../../contexts/CommonItensProvider";
import {
    getSelectable,
    computePreviewSet,
    resolvePair,
    computeGroupUpdate,
    buildCells,
    statusColors,
    isMotorOcupado,
    GROUP_BORDER,
    LEGEND,
    findIdxByNumeroMotor,
} from './config/functions';
import { TutorialMachineSecondStepPanel } from "./components/TutorialMachineSecondStepPanel";

export function SecondStepMachineForm({ qtdMotores, motores, setMotores }) {
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const { dadosUsuario } = useAppContext();
    const { exibirAlerta } = useCommonItems();

    const cols = qtdMotores === 60 ? 10 : 6;
    const sz = isSmall ? 36 : 48;
    const gap = 8;

    const [firstIdx, setFirstIdx] = useState(null);
    const [hoverIdx, setHoverIdx] = useState(null);

    const [openUngroupDialog, setOpenUngroupDialog] = useState(false);
    const [selectedMotor, setSelectedMotor] = useState(null);

    useEffect(() => {
        const handler = e => { if (e.key === 'Escape') cancelSel(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, []);

    useEffect(() => { setFirstIdx(null); setHoverIdx(null); }, [motores.length, cols]);

    const cancelSel = () => { setFirstIdx(null); setHoverIdx(null); };

    const previewSet = computePreviewSet(firstIdx, hoverIdx, motores, cols);

    const applyGroup = (selectedMotorIndex, targetMotorIndex) => {
        setMotores(computeGroupUpdate(motores, selectedMotorIndex, targetMotorIndex));
    };

    const handleClick = (physIdx) => {
        if (!dadosUsuario.isSempher) return;
        const motor = motores[physIdx];

        // Bloqueado: precisa desbloquear antes (tooltip já avisa).
        if (motor.status === 'bloqueado' || motor.status === 'inativo') return;

        if (motor.agrupado_por) {
            if (firstIdx !== null) { cancelSel(); return; }
            setSelectedMotor(motor);
            setOpenUngroupDialog(true);
            return;
        }

        if (firstIdx === null) {
            if (getSelectable(physIdx, motores, cols).length === 0) return;
            setFirstIdx(physIdx);
            setHoverIdx(physIdx);
            return;
        }

        if (physIdx === firstIdx) { cancelSel(); return; }

        if (getSelectable(firstIdx, motores, cols).includes(physIdx)) {
            const { firstMotorIndex, targetMotorIndex } = resolvePair(firstIdx, physIdx, motores);

            // Ocupado NUNCA agrupa: precisa remover o produto antes.
            if (isMotorOcupado(motores[firstMotorIndex]) || isMotorOcupado(motores[targetMotorIndex])) {
                exibirAlerta(
                    'Motor ocupado',
                    'Remova o produto dos motores antes de agrupá-los.',
                    'warning'
                );
                cancelSel();
                return;
            }

            applyGroup(firstMotorIndex, targetMotorIndex);
            cancelSel();
        } else {
            if (getSelectable(physIdx, motores, cols).length > 0) {
                setFirstIdx(physIdx);
                setHoverIdx(physIdx);
            } else {
                cancelSel();
            }
        }
    };

    const handleConfirmUngroupAction = () => {
        if (selectedMotor && selectedMotor.numero_motor) {
            const selectedMotorIndex = findIdxByNumeroMotor(motores, selectedMotor.numero_motor);
            const targetMotorIndex = findIdxByNumeroMotor(motores, selectedMotor.agrupado_por);

            if (selectedMotorIndex !== -1 && targetMotorIndex !== -1) {
                applyGroup(selectedMotorIndex, targetMotorIndex);
            }
        }

        setOpenUngroupDialog(false);
        setSelectedMotor(null);
    };

    const rows  = motores.length > 0 ? Math.ceil(motores.length / cols) : 1;
    const cells = buildCells(motores, cols);

    const getCellSx = (cell) => {
        const { motor, physIdx, gridRow, gridCol, colSpan, grouped } = cell;

        const isFirst = physIdx === firstIdx;
        const isPrev  = previewSet.has(physIdx) && !isFirst;
        // Escurece tudo que fica indisponível durante a seleção (inclui agrupados).
        const dimmed  = isDisabledDuringSelection(cell) && !isPrev;

        const w = colSpan * sz + (colSpan - 1) * gap;

        const base = {
            gridColumn: `${gridCol} / span ${colSpan}`,
            gridRow: `${gridRow} / span 1`,
            width: `${w}px`, height: `${sz}px`,
            minWidth: 0, p: 0,
            fontSize: 12, fontWeight: 500, borderRadius: 1.5,
            transition: 'background-color .15s, border-color .15s, opacity .15s, transform .15s',
            opacity: dimmed ? 0.3 : 1,
        };

        if (grouped) {
            const ocupado = isMotorOcupado(motor) || (cell.partner && isMotorOcupado(cell.partner));
            const c = ocupado ? statusColors('ocupado') : statusColors(motor.status);
            return {
                ...base,
                backgroundColor: c.bg,
                border: GROUP_BORDER,
                color: c.fg,
                '&:hover': { backgroundColor: c.hover },
                '&.Mui-disabled': { backgroundColor: c.bg, color: c.fg, opacity: dimmed ? 0.3 : 1, border: GROUP_BORDER },
            };
        }

        if (isFirst) {
            return { ...base, backgroundColor: amber[300], border: `2px solid ${amber[600]}`, color: amber[900], transform: 'scale(1.05)', '&:hover': { backgroundColor: amber[400] } };
        }

        if (isPrev) {
            return { ...base, backgroundColor: blue[100], border: `1.5px solid ${blue[400]}`, color: blue[900], '&:hover': { backgroundColor: blue[200] } };
        }

        const c = statusColors(motor.status);
        return {
            ...base,
            backgroundColor: c.bg,
            border: `1.5px solid ${c.bd}`,
            color: c.fg,
            '&:hover': { backgroundColor: c.hover },
            '&.Mui-disabled': { backgroundColor: c.bg, color: c.fg, opacity: dimmed ? 0.3 : 1 },
        };
    };

    // Durante uma seleção (firstIdx ativo): só o próprio selecionado e os
    // disponíveis para agrupar continuam habilitados; o resto fica disabled.
    const isDisabledDuringSelection = (cell) => {
        if (firstIdx === null) return false;

        const selectable = getSelectable(firstIdx, motores, cols);
        const isFirst = cell.physIdx === firstIdx;
        const isSelectable =
            selectable.includes(cell.physIdx) ||
            (cell.partnerIdx !== undefined && selectable.includes(cell.partnerIdx));

        return !isFirst && !isSelectable;
    };

    const getTooltipTitle = (cell) => {
        if (firstIdx !== null && isDisabledDuringSelection(cell)) {
            return 'Motor indisponível para agrupar com o motor selecionado.';
        }
        if (cell.motor.status === 'bloqueado') {
            return 'Motor bloqueado — desbloqueie antes de agrupar.';
        }

        const ocupado =
            isMotorOcupado(cell.motor) ||
            (cell.partner && isMotorOcupado(cell.partner));

        if (cell.grouped) {
            return ocupado
                ? 'Motores agrupados e ocupados — remova o produto antes de desfazer.'
                : 'Clique para desfazer o agrupamento';
        }
        if (ocupado) {
            return 'Motor ocupado — remova o produto antes de agrupar.';
        }
        return '';
    };

    return (
        <Grid container direction="row" justifyContent="center" alignItems="center" spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
                <Typography variant="h5" textAlign="center" gutterBottom>
                    {dadosUsuario.isSempher ? 'Deseja agrupar os motores?' : 'Mapeamento de motores'}
                </Typography>
            </Grid>

            <Grid item xs={12} md={7}>
                <Box
                    sx={{ border: `1px solid ${grey[300]}`, borderRadius: 2, p: 2 }}
                    onMouseLeave={() => firstIdx !== null && setHoverIdx(firstIdx)}
                >
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${cols}, ${sz}px)`,
                        gridTemplateRows:    `repeat(${rows}, ${sz}px)`,
                        gap: `${gap}px`,
                        justifyContent: 'center',
                    }}>
                        {cells.map((cell) => (
                            <Tooltip key={cell.physIdx} title={getTooltipTitle(cell)} placement="top" arrow>
                                <span style={{
                                    gridColumn: `${cell.gridCol} / span ${cell.colSpan}`,
                                    gridRow: `${cell.gridRow} / span 1`,
                                }}>
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        fullWidth
                                        disabled={
                                            !dadosUsuario.isSempher
                                            || cell.motor.status === 'bloqueado'
                                            || cell.motor.status === 'inativo'
                                            || isDisabledDuringSelection(cell)
                                        }
                                        onClick={() => {
                                            if (cell.grouped) {
                                                if (!dadosUsuario.isSempher) return;
                                                setSelectedMotor(cell.childMotor);
                                                setOpenUngroupDialog(true);
                                                return;
                                            }
                                            handleClick(cell.physIdx);
                                        }}
                                        onMouseEnter={() => firstIdx !== null && setHoverIdx(cell.physIdx)}
                                        sx={getCellSx(cell)}
                                    >
                                        {cell.grouped
                                            ? `${cell.motor.numero_motor} + ${cell.partner.numero_motor}`
                                            : cell.motor.numero_motor}
                                    </Button>
                                </span>
                            </Tooltip>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, mt: 1.5, pl: .5 }}>
                    {LEGEND.map(l => (
                        <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: .75 }}>
                            <Box sx={{
                                width: 14, height: 14, borderRadius: '3px',
                                backgroundColor: l.bg,
                                border: l.dashed ? `2px dashed ${l.border}` : `1.5px solid ${l.border}`,
                            }} />
                            <Typography variant="caption" color="text.secondary">{l.label}</Typography>
                        </Box>
                    ))}
                </Box>
            </Grid>

            {dadosUsuario.isSempher && (
                <Grid item xs={12} md={4}>
                    <TutorialMachineSecondStepPanel />
                </Grid>
            )}

            <ConfirmDialog
                title={'Tem certeza que deseja desfazer o agrupamento?'}
                description={'Ao confirmar, os dois motores serão desagrupados e todos os dados (produto, estoque e configurações) serão resetados.'}
                goAction={handleConfirmUngroupAction}
                handleClose={() => {
                    setSelectedMotor(null);
                    setOpenUngroupDialog(false);
                }}
                state={openUngroupDialog}
            />
        </Grid>
    );
}